import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, ClipboardList, Gauge, Settings, Users } from "lucide-react";
import { api } from "../../../services/apiService";
import { useAuth } from "../../../context/AuthContext";
import { Badge } from "../../../components/ops/Badge";
import { EmptyState } from "../../../components/ops/EmptyState";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Sparkbars } from "../../../components/ops/Sparkbars";
import { StatCard } from "../../../components/ops/StatCard";
import { todayLabel } from "../../../utils/greeting";
import { countSince, upcomingCounts } from "../../../utils/trends";

const quickActions = [
  { to: "/coach/schedule", label: "View my schedule", description: "See upcoming sessions and rosters", icon: CalendarClock },
  { to: "/coach/assessments", label: "Record an assessment", description: "Score a player's latest evaluation", icon: ClipboardList },
  { to: "/coach/players", label: "Review players", description: "Browse the players you coach", icon: Users },
  { to: "/coach/settings", label: "Account settings", description: "Update your coach profile", icon: Settings },
];

export function CoachDashboardPage() {
  const { user, accessToken } = useAuth();
  const [players, setPlayers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    Promise.all([
      api.get("/dashboard/summary"),
      api.get("/schedule/sessions", { from: new Date().toISOString(), coachId: user.id }),
    ])
      .then(([summaryResponse, sessionsResponse]) => {
        if (!active) return;
        setPlayers(summaryResponse.data.players || []);
        setSessions(sessionsResponse.data.sessions || []);
      })
      .catch(() => { if (active) setError("Unable to load dashboard data"); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [accessToken, user.id]);

  const stats = useMemo(() => {
    const assessed = players.filter((entry) => entry.latestAssessment);
    const avgIndex = assessed.length
      ? Math.round(assessed.reduce((sum, entry) => sum + (entry.formulaPlayerIndex || 0), 0) / assessed.length)
      : null;
    const now = new Date();
    const todaysSessions = sessions.filter((s) => new Date(s.startsAt).toDateString() === now.toDateString());
    const assessedThisWeek = countSince(assessed.map((entry) => entry.latestAssessment));

    return {
      totalPlayers: players.length,
      upcomingSessions: sessions.length,
      todaysSessions: todaysSessions.length,
      assessedCount: assessed.length,
      unassessed: players.length - assessed.length,
      assessedThisWeek,
      avgIndex,
    };
  }, [players, sessions]);

  const sessionsThisWeek = useMemo(() => upcomingCounts(sessions), [sessions]);
  const nextSessions = useMemo(() => sessions.slice(0, 5), [sessions]);

  return (
    <div>
      <PageHeader title={`Welcome back, ${user.firstName || "Coach"}`} subtitle={`${todayLabel()} · Here's what's ahead for your training program`} />

      <div className="ops-stat-grid">
        <StatCard icon={Users} label="My players" value={loading ? "—" : stats.totalPlayers} hint="Across your rosters" />
        <StatCard icon={CalendarClock} label="Upcoming sessions" value={loading ? "—" : stats.upcomingSessions} hint={`${stats.todaysSessions} today`} />
        <StatCard
          icon={ClipboardList}
          label="Players assessed"
          value={loading ? "—" : stats.assessedCount}
          hint={`of ${stats.totalPlayers} players`}
          trend={!loading && stats.assessedThisWeek > 0 ? { label: `+${stats.assessedThisWeek} this week` } : null}
        />
        <StatCard icon={Gauge} label="Avg. Formula Player Index" value={loading ? "—" : stats.avgIndex ?? "—"} hint="Latest assessments" />
      </div>

      {error && <div className="ops-alert ops-alert--error">{error}</div>}

      <div className="ops-columns">
        <div className="ops-card">
          <h2 className="ops-card-title">Upcoming sessions</h2>
          {loading ? (
            <p className="ops-loading">Loading sessions…</p>
          ) : nextSessions.length === 0 ? (
            <EmptyState icon={CalendarClock} title="No upcoming sessions" description="Schedule a session to see it here." />
          ) : (
            <ul className="ops-list">
              {nextSessions.map((session) => (
                <li key={session._id} className="ops-list-item">
                  <div className="ops-list-item-avatar"><CalendarClock size={16} /></div>
                  <div className="ops-list-item-body">
                    <p className="ops-list-item-title">{session.title}</p>
                    <p className="ops-list-item-meta">
                      {new Date(session.startsAt).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {session.location}
                    </p>
                  </div>
                  {session.program?.name && <Badge tone="accent">{session.program.name}</Badge>}
                </li>
              ))}
            </ul>
          )}
          <Link to="/coach/schedule" className="ops-card-link">
            View full schedule <ArrowRight size={14} />
          </Link>
        </div>

        <div className="ops-stack">
          <div className="ops-card">
            <div className="ops-activity-header">
              <h2 className="ops-card-title" style={{ margin: 0 }}>This week's sessions</h2>
              {!loading && <Sparkbars data={sessionsThisWeek} direction="future" />}
            </div>
            <p className="ops-activity-total">
              <strong>{loading ? "—" : stats.upcomingSessions}</strong> {stats.upcomingSessions === 1 ? "session" : "sessions"} scheduled over the next 7 days
            </p>

            <ul className="ops-attention-list">
              {!loading && stats.todaysSessions > 0 && (
                <li>
                  <Link to="/coach/schedule" className="ops-attention-item">
                    <span className="ops-attention-item-label">
                      <span className="ops-attention-item-dot" />
                      {stats.todaysSessions} {stats.todaysSessions === 1 ? "session" : "sessions"} today
                    </span>
                    <ArrowRight size={14} />
                  </Link>
                </li>
              )}
              {!loading && stats.unassessed > 0 && (
                <li>
                  <Link to="/coach/assessments" className="ops-attention-item">
                    <span className="ops-attention-item-label">
                      <span className="ops-attention-item-dot" />
                      {stats.unassessed} {stats.unassessed === 1 ? "player" : "players"} awaiting assessment
                    </span>
                    <ArrowRight size={14} />
                  </Link>
                </li>
              )}
              {!loading && stats.todaysSessions === 0 && stats.unassessed === 0 && (
                <li className="ops-attention-empty">You're all caught up — nothing needs attention.</li>
              )}
            </ul>
          </div>

          <div className="ops-card">
            <h2 className="ops-card-title">Quick actions</h2>
            <div className="ops-action-grid">
              {quickActions.map(({ to, label, description, icon: Icon }) => (
                <Link key={to} to={to} className="ops-action-card">
                  <div className="ops-action-card-icon">
                    <Icon size={20} strokeWidth={1.8} />
                  </div>
                  <div className="ops-action-card-content">
                    <h3>{label}</h3>
                    <p>{description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
